import { SessionStorageKeys } from '@aglenergy/shared-library';
import { store } from 'app/shared/store/store';
import { bundleSearchService } from 'app/bundle/services/bundle-search.service';
import { guidedFlowActiveCategoryHandler } from 'app/bundle/store/handlers/guidedFlowActiveCategory.handler';
import { cartHandler } from 'app/bundle/store/handlers/cart.handler';
import { addressRequiredHandler, flowTypeHandler } from 'app/bundle/store/handlers/flow.type.handler';
import { addAddressHandler } from 'app/bundle/store/handlers/bundle.address.handler';
import { removeHandler } from 'app/shared/store/handlers/types/remove-handler';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';
import { nbnProductService } from 'app/nbn/services/nbn-product/nbn-product.service';
import { productsSessionStorageService } from 'app/shared/services/product-session-storage/product-session-storage.service';
import { delayMessageService } from 'app/shared/services/delay-message/delay-message.service';
import { authenticationService } from 'app/shared/services/authentication/authentication.service';
import { BundleSearchResponse } from 'app/shared/models/bundle-search-response.model';
import { flowService } from 'app/shared/services/flow/flow-service';
import { isLocalDevelopment } from 'app/shared/utilities/helper-functions';
import { locationService } from 'app/shared/services/location/location-service';
import { localStorageService } from 'app/shared/services/local-storage/local-storage.service';

const registeredHandlers = (isBundleLandingPage = false): removeHandler => {
  const removeAddressHandler = addAddressHandler(store, isBundleLandingPage);
  const removeFlowTypeHandler = flowTypeHandler(store);
  const removeCartHandler = cartHandler(store);
  const removeGuidedFlowActiveCategoryHandler = guidedFlowActiveCategoryHandler(store);
  const removeAddressRequiredHandler = addressRequiredHandler(store);
  return () => {
    removeAddressHandler();
    removeFlowTypeHandler();
    removeCartHandler();
    removeGuidedFlowActiveCategoryHandler();
    removeAddressRequiredHandler();
  };
};

export const onStoreInit = async (isGuidedFlow: boolean, isBundleLandingPage = false): Promise<void> => {
  registeredHandlers(isBundleLandingPage);
  const addressInSessionStorage = sessionStorage.getItem(SessionStorageKeys.address);
  const address = addressInSessionStorage && addressInSessionStorage !== 'undefined' ? JSON.parse(addressInSessionStorage) : null;
  store.set(StoreKeys.isGuidedFlow, isGuidedFlow);
  if (isGuidedFlow) {
    const flow = flowService.getFlowDetails();
    const addressRequired = flow?.addressRequired;
    const pathName = isLocalDevelopment() ? '/apps/comparison/residential/plan-builder' : '/residential/plan-builder';
    store.set('addressRequired', addressRequired);
    store.set('flowType', 'guided-flow');

    if (!sessionStorage.getItem(SessionStorageKeys.bundleProducts) && window.location.pathname === pathName && !flow?.error) {
      locationService.navigateToProductBundleLanding();
    }

    if (!isBundleLandingPage) {
      const productSessionData = productsSessionStorageService.getLatestProductsByProductCategory('nbn');
      await nbnProductService.getVoipProducts(productSessionData?.address?.state).then((voipProductDetails) => {
        store.set('voipProductDetails', voipProductDetails);
      });
      await nbnProductService.getModemProducts(productSessionData?.address?.state).then((modemProductDetails) => {
        store.set('modemProductDetails', modemProductDetails);
      });
      const isAuthenticated = authenticationService.loggedIn;
      store.set('isAuthenticated', isAuthenticated);
      if (
        isAuthenticated &&
        window.sessionStorage.getItem('authenticatedGuidedFlowEnabled')?.includes('true') &&
        !store.get('addressRequired')
      ) {
        await bundleSearchService
          .getAuthenticatedProducts()
          .then((productDetails) => {
            const bundleProducts: BundleSearchResponse = productDetails as BundleSearchResponse;
            const firstAvailableCategory = bundleSearchService.getFirstAvailableProductCategory(bundleProducts, true);
            store.set('address', productDetails.contractAddress);
            if (isBundleLandingPage && firstAvailableCategory !== 'energy') {
              if (firstAvailableCategory === 'nbn') {
                const bundleSearchStore = store.get(StoreKeys.bundleSearchStore);
                if (bundleSearchStore) {
                  bundleSearchStore.energyResponse.siteError = {
                    reasonCode: 'serviceNotAvailable',
                    description: 'Energy not available, NBN available'
                  };
                  store.set(StoreKeys.bundleSearchStore, bundleSearchStore);
                }
              } else {
                const bundleSearchStore = store.get(StoreKeys.bundleSearchStore);
                if (bundleSearchStore) {
                  bundleSearchStore.energyResponse.siteError = {
                    reasonCode: 'serviceNotAvailable',
                    description: 'Energy not available, NBN not available'
                  };
                  store.set(StoreKeys.bundleSearchStore, bundleSearchStore);
                }
              }
            } else {
              // As long as energy is available, then will not show error modal and go to bundle builder page
              store.set(StoreKeys.guidedFlowActiveCategory, firstAvailableCategory);
            }
          })
          .catch((error) => {
            store.set(StoreKeys.responseError, error);
          })
          .finally(() => {
            store.set('isPersonalisedProductsLoading', false);
            store.set('isPageLoaded', true);
            delayMessageService.stopAllTimers();
          });
      }
    }
  }

  if (address) {
    store.set('address', address);
    localStorageService.setRegion(address.state);
  }
  // TODO: TealiumAnalytic will be added
};
